import { Button, Tooltip, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { DiamondFilled, DiamondRegular } from '@fluentui/react-icons';
import { STAGE_STATUS_METADATA } from '../constants';
import useProjectStagesStore from '../stores/useProjectStagesStore';
import { EStageStatus, type TStage } from '../types';

const useStyles = makeStyles({
  root: { overflowX: 'auto', padding: tokens.spacingVerticalXS },
  list: { display: 'flex', alignItems: 'center', minWidth: 'min-content' },
  stage: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS, flexShrink: 0 },
  connected: { flexGrow: 1 },
  title: {
    minWidth: 0,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    whiteSpace: 'nowrap',
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
  },
  current: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground2,
    backgroundColor: tokens.colorBrandBackground2,
    ':hover': { color: tokens.colorBrandForeground2Hover, backgroundColor: tokens.colorBrandBackground2Hover },
    ':active': { color: tokens.colorBrandForeground2Pressed, backgroundColor: tokens.colorBrandBackground2Pressed },
  },
  cancelled: { color: tokens.colorPaletteRedForeground1, textDecorationLine: 'line-through' },
  connector: { display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: '32px' },
  line: {
    flexGrow: 1,
    minWidth: tokens.spacingHorizontalS,
    height: tokens.strokeWidthThin,
    backgroundColor: tokens.colorNeutralStroke2,
  },
  gate: { flexShrink: 0, color: tokens.colorNeutralForeground3 },
  gateCompleted: { color: tokens.colorNeutralForeground2 },
});

type TProps = { projectId: string; stages: TStage[] };

const StagesTimeline = ({ projectId, stages }: TProps) => {
  const setStageDialogState = useProjectStagesStore((state) => state.setStageDialogState);
  const open = (stage: TStage) => setStageDialogState({ isOpen: true, projectId, stageIndex: stage.index });
  const classes = useStyles();
  return (
    <div className={classes.root}>
      <div className={classes.list}>
        {stages.map((stage, index) => {
          const isCompleted = stage.status === EStageStatus.COMPLETED;
          const GateIcon = isCompleted ? DiamondFilled : DiamondRegular;
          const gate = stage.gate && (
            <Tooltip content={`Gate: ${stage.gate.title}`} relationship="label" withArrow>
              <Button
                appearance="transparent"
                size="small"
                className={mergeClasses(classes.gate, isCompleted && classes.gateCompleted)}
                icon={<GateIcon />}
                onClick={() => open(stage)}
              />
            </Tooltip>
          );
          const isLast = index === stages.length - 1;
          return (
            <div key={stage.index} className={mergeClasses(classes.stage, !isLast && classes.connected)}>
              <Tooltip content={STAGE_STATUS_METADATA[stage.status].label} relationship="description" withArrow>
                <Button
                  appearance="transparent"
                  size="small"
                  className={mergeClasses(
                    classes.title,
                    stage.status === EStageStatus.CURRENT && classes.current,
                    stage.status === EStageStatus.CANCELLED && classes.cancelled
                  )}
                  onClick={() => open(stage)}
                >
                  {stage.title}
                </Button>
              </Tooltip>
              {isLast ? (
                gate
              ) : (
                <div className={classes.connector}>
                  <div className={classes.line} />
                  {gate}
                  <div className={classes.line} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StagesTimeline;
